import * as React from 'react';
import {ThemeProvider} from '@mui/material/styles';
import {useEffect, useState} from 'react';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {FlexeDataGrid} from '../../shared/mui/flexe-data-grid/FlexeDataGrid';
import {flexeMuiTheme} from '../../shared/mui/default-mui-theme';
import InventoryService, {PaginatedInventory} from '../../shared/services/InventoryService';
import {CustomFilterToolBar} from '../../shared/mui/flexe-data-grid/CustomFilterToolBar';
import ClickableRow from '../../shared/mui/flexe-data-grid/ClickableRow';
import {ITEM_MASTER_GRID_COLUMNS, ItemMasterGridContent} from './ItemMasterGridColumnDefinition';

const DEFAULT_PAGE_SIZE = 20;

interface ItemMasterTableProps extends RouteComponentProps {
  authenticityToken: string;
  isShipper: boolean;
  reservationId?: number;
}

export const ItemMasterTableComponent = (props: ItemMasterTableProps) => {
  const inventoryService = new InventoryService(props.authenticityToken);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [continuationTokens, setContinuationTokens] = useState<string[]>(['']);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rows, setRows] = useState<ItemMasterGridContent[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [skuFilter, setSkuFilter] = useState<string>('');

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });

  const clearState = () => {
    setContinuationTokens(['']);
    setRows([]);
    setTotalCount(0);
  };

  // Clear state when reservationId or skuFilter are set before
  useEffect(() => {
    clearState();
    setPaginationModel((prev) => ({...prev, page: 0}));
  }, [props.reservationId, skuFilter]);

  // Used to fetch Inventory Summary
  useEffect(() => {
    if (!props.reservationId) {
      setLoading(false);
      return;
    }
    fetchInventorySummary();
  }, [paginationModel, skuFilter, props.reservationId]);

  const fetchInventorySummary = async () => {
    setErrors([]);
    setLoading(true);

    try {
      const tokenForCurrentPage = continuationTokens[paginationModel.page];

      const response = await inventoryService.getInventories(
        props.reservationId > 0 ? props.reservationId : null,
        skuFilter === '' ? null : skuFilter,
        tokenForCurrentPage === '' ? null : tokenForCurrentPage,
        props.isShipper,
        false,
        paginationModel.pageSize
      );
      const mappedRows = response.inventory_summary.map((summary) => mapSummary(summary));

      setContinuationTokens((prevTokens) => {
        const newTokens = [...prevTokens];
        newTokens[paginationModel.page + 1] = response.continuation_token;
        return newTokens;
      });
      setTotalCount(response.result_set_size);
      setRows(mappedRows);
    } catch (e) {
      setErrors([e.toString()]);
    } finally {
      setLoading(false);
    }
  };

  const mapSummary = (invSum: PaginatedInventory): ItemMasterGridContent => {
    return {
      id: invSum.id,
      itemCode: invSum.item_code,
      description: invSum.description,
      palletCount: invSum.pallet_count,
      cartonCount: invSum.available_cartons,
      storedCount: invSum.stored,
      allocatedCount: invSum.allocated,
      receivedCount: invSum.received,
      onHoldCount: invSum.on_hold,
      damagedCount: invSum.damaged_available_eaches,
      availableCount: invSum.available
    };
  };

  const errorDisplay = errors.map((error, idx) => {
    return (
      <div className="alert alert-danger" role="alert" key={`error:${idx}`}>
        {error}
      </div>
    );
  });

  const handlePaginationModelChange = (newPaginationModel: any) => {
    if (newPaginationModel.pageSize !== paginationModel.pageSize) {
      clearState();
    }
    setPaginationModel(newPaginationModel);
  };

  const handleFilterChange = (value: string) => {
    if (value !== skuFilter) {
      clearState();
      setSkuFilter(value);
    }
  };

  const getInventoryLink = (row) => `${props.isShipper ? '/s' : '/wh'}/inventories/${row.id}`;

  const datagridSx = {
    '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {py: '8px'},

    // Style the column headers
    '& .MuiDataGrid-columnHeaderTitleContainerContent': {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      width: '100%'
    },

    '& .MuiDataGrid-cell:focus': {
      // Remove focus styles
      outline: 'none'
    },

    '& .MuiInputBase-inputTypeSearch': {
      fontSize: '14px'
    }
  };

  return (
    <div className="item-master-table">
      <ThemeProvider theme={flexeMuiTheme}>
        {errorDisplay}
        <FlexeDataGrid
          autoHeight
          columns={ITEM_MASTER_GRID_COLUMNS}
          rows={rows}
          loading={isLoading}
          getRowId={(row) => row.itemCode}
          pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={totalCount}
          paginationMode="server"
          // columnVisibilityModel={columnVisibilityModel} // TODO: might need this for crossdock
          getRowHeight={() => 'auto'}
          sx={datagridSx}
          disableColumnMenu
          disableColumnSelector
          disableColumnFilter
          disableDensitySelector
          disableRowSelectionOnClick
          slots={{
            toolbar: CustomFilterToolBar,
            row: ClickableRow
          }}
          slotProps={{
            toolbar: {
              placeholderText: 'Type to filter...',
              onFilterChange: handleFilterChange,
              debounceMs: 500
            },
            row: {
              getHref: getInventoryLink
            }
          }}
        />
      </ThemeProvider>
    </div>
  );
};

export const ItemMasterTable = withRouter(ItemMasterTableComponent);
